import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  NavigateNext as NavigateNextIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingIcon,
  Cancel as CancelIcon,
  TrendingFlat as TrendingFlatIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import PageWithHeader from '../common/PageWithHeader';
import { useRouter } from '../../hooks/useRouter';
import { useAppSelector } from '../../redux/hooks';
import { useGetUsersQuery, useGetAllSpearReviewsQuery, usePatchEmployeeReviewByIdCompleteGoalMutation, MonthlyIncentiveGoal } from '../../redux/vmsApi';
import { getGoalAchievementStatus, getGoalStatusColor, GoalAchievementStatus } from '../../utils/goalUtils';
import { useGetAllPEARReviewsQuery, PEARReview } from '../../redux/pearReviewApi';
import { useGetDepartmentStructureQuery, useGetJobDescriptionsByDepartmentQuery, useGetMyJobDescriptionQuery } from '../../redux/jobDescriptionApi';
import { useGetOrgChartWithBSCQuery, OrgChartNode } from '../../redux/balancedScorecardApi';
import { useGetCastingToolDepartmentDetailQuery } from '../../redux/castingToolApi';
import { PERFORMANCE_RATINGS } from './bscTypes';

// Generate quarters for select dropdown
const generateQuarterOptions = (year: number) => {
  return [
    { value: `Q1 ${year}`, label: `Q1 ${year}` },
    { value: `Q2 ${year}`, label: `Q2 ${year}` },
    { value: `Q3 ${year}`, label: `Q3 ${year}` },
    { value: `Q4 ${year}`, label: `Q4 ${year}` },
  ];
};

// Check if a monthly period (e.g., "March 2026") is March 2026 or later
// Monthly Incentive Goals feature started in March 2026
const isMonthMarch2026OrLater = (monthPeriod: string): boolean => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
  const match = monthPeriod.match(/^(\w+)\s+(\d{4})$/);
  if (!match) return false;
  const [, monthName, yearStr] = match;
  const year = parseInt(yearStr);
  const monthIndex = monthNames.indexOf(monthName); // 0-11
  // March 2026 = year 2026, monthIndex 2
  // Returns true if (year > 2026) OR (year === 2026 AND monthIndex >= 2)
  return (year > 2026) || (year === 2026 && monthIndex >= 2);
};

// Grade color helper
const getGradeColor = (grade?: string): string => {
  if (!grade) return '#9e9e9e';
  const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
  return rating?.colorCode || '#9e9e9e';
};

// Score color helper
const getScoreColor = (score?: number): string => {
  if (score === undefined || score === null) return '#9e9e9e';
  if (score >= 110) return '#033e79';
  if (score >= 100) return '#17A2B8';
  if (score >= 85) return '#32CD32';
  if (score >= 70) return '#FFC107';
  return '#DC3545';
};

// Get initials from name
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Employee with scores type
interface EmployeeScore {
  id: string;
  name: string;
  email: string;
  designation?: string;
  bscScore?: number;
  bscGrade?: string;
  bscStatus?: string;
  pearScore?: number;
  pearGrade?: string;
  pearStatus?: string;
  hasReviews: boolean;
  // Goal tracking
  monthlyGoals: {
    spearId: string;
    month: string;
    shortMonth: string;
    goalDescription?: string;
    goalMeasure?: string;
    targetPercentage?: number;
    actualPercentage?: number;
    status: GoalAchievementStatus;
    completedByUserName?: string;
  }[];
  // Aggregated goal stats for sorting
  goalsAchieved: number;
  goalsProgress: number;
  goalsFailed: number;
}

// Sort configuration
type SortField = 'name' | 'designation' | 'bscScore' | 'pearScore' | 'goals';
type SortDirection = 'asc' | 'desc';

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  secondarySubtitle?: string;
  icon: React.ReactNode;
  color: string;
  showWarning?: boolean;
  warningText?: string;
}> = ({ title, value, subtitle, secondarySubtitle, icon, color, showWarning, warningText }) => (
  <Card sx={{ height: '100%', border: showWarning ? '2px solid #ff9800' : undefined }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={color}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {subtitle}
            </Typography>
          )}
          {secondarySubtitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {secondarySubtitle}
            </Typography>
          )}
          {showWarning && warningText && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
              <WarningIcon fontSize="small" sx={{ color: '#ff9800', fontSize: 14 }} />
              <Typography variant="caption" color="warning.main">
                {warningText}
              </Typography>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(color, 0.1),
            color: color,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// Grade Distribution Mini Chart
const GradeDistributionMini: React.FC<{ employees: EmployeeScore[] }> = ({ employees }) => {
  const grades = employees
    .filter(e => e.bscGrade)
    .reduce((acc, e) => {
      const grade = e.bscGrade!;
      acc[grade] = (acc[grade] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const total = Object.values(grades).reduce((sum, count) => sum + count, 0);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {Object.entries(grades)
        .sort((a, b) => b[1] - a[1])
        .map(([grade, count]) => (
          <Chip
            key={grade}
            label={`${grade}: ${count} (${((count / total) * 100).toFixed(0)}%)`}
            size="small"
            sx={{
              backgroundColor: getGradeColor(grade),
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        ))}
    </Box>
  );
};

// Department Detail View Component
const CEODepartmentView: React.FC = () => {
  const router = useRouter();
  const { department } = useParams<{ department: string }>();
  const [searchParams] = useSearchParams();
  const periodFromUrl = searchParams.get('period');
  const currentUser = useAppSelector(state => state.auth.user);
  const currentYear = new Date().getFullYear();

  // Check if user has company-wide view (CEO, COO, admin)
  const hasCompanyWideView = currentUser?.roles?.includes('admin') || 
    currentUser?.roles?.includes('ceo') || 
    currentUser?.roles?.includes('coo');

  const availableQuarters = useMemo(() => [
    ...generateQuarterOptions(currentYear),
    ...generateQuarterOptions(currentYear - 1),
  ], [currentYear]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(periodFromUrl || availableQuarters[0]?.value || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('bscScore');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const decodedDepartment = decodeURIComponent(department || '');
  const isCSuiteDepartment = decodedDepartment === 'C-Suite';

  // Casting Tool always uses current month (it's about current commitments, not historical reviews)
  const castingToolPeriod = useMemo(() => {
    const monthNames = [
      'January', 'February', 'March',
      'April', 'May', 'June',
      'July', 'August', 'September',
      'October', 'November', 'December'
    ];
    const now = new Date();
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  }, []);

  // Fetch data (skip department query for C-Suite virtual department)
  const { data: jobDescriptions, isLoading: loadingJDs } = useGetJobDescriptionsByDepartmentQuery(
    { department: decodedDepartment },
    { skip: !decodedDepartment || isCSuiteDepartment }
  );
  const { data: myJobDescription, isLoading: loadingMyJD } = useGetMyJobDescriptionQuery();
  const { data: pearReviews, isLoading: loadingPEAR, refetch: refetchPEAR } = useGetAllPEARReviewsQuery();
  const { data: orgChartData, isLoading: loadingOrgChart, refetch: refetchOrgChart } = useGetOrgChartWithBSCQuery({ period: selectedPeriod });
  const { data: allSpearReviews, isLoading: loadingSpear, refetch: refetchSpear } = useGetAllSpearReviewsQuery();
  const { data: castingToolData, isLoading: loadingCastingTool, refetch: refetchCastingTool } = useGetCastingToolDepartmentDetailQuery(
    { department: decodedDepartment, reviewPeriod: castingToolPeriod },
    { skip: !decodedDepartment || isCSuiteDepartment }
  );
  const [markGoalComplete] = usePatchEmployeeReviewByIdCompleteGoalMutation();

  const isLoading = loadingJDs || loadingMyJD || loadingPEAR || loadingOrgChart || loadingSpear || loadingCastingTool;

  // Calculate casting tool metrics for the department
  const castingToolMetrics = useMemo(() => {
    if (!castingToolData) return { total: 0, submitted: 0, rate: 0 };
    const total = castingToolData.length;
    const submitted = castingToolData.filter(e => e.hasSubmitted).length;
    return {
      total,
      submitted,
      rate: total > 0 ? (submitted / total) * 100 : 0,
    };
  }, [castingToolData]);

  // Get current user's department and job description ID
  const myDepartment = myJobDescription?.department;
  const myJobDescriptionId = myJobDescription?.id;

  // Helper function to recursively find all subordinates
  const getAllSubordinateIds = useMemo(() => {
    if (!orgChartData || !myJobDescriptionId) return new Set<string>();
    
    const subordinateIds = new Set<string>();
    
    // Create a map of job description ID to their direct reports
    const reportsToMap = new Map<string, string[]>();
    orgChartData.forEach(node => {
      const reportsTo = node.jobDescription.reportsToJobDescriptionId;
      if (reportsTo) {
        if (!reportsToMap.has(reportsTo)) {
          reportsToMap.set(reportsTo, []);
        }
        reportsToMap.get(reportsTo)!.push(node.jobDescription.id);
      }
    });
    
    // Create a map of job description ID to employee ID
    const jdToEmployeeMap = new Map<string, string>();
    orgChartData.forEach(node => {
      if (node.employee?.id) {
        jdToEmployeeMap.set(node.jobDescription.id, node.employee.id);
      }
    });
    
    // Recursively find all subordinates
    const findSubordinates = (jdId: string) => {
      const directReports = reportsToMap.get(jdId) || [];
      directReports.forEach(reportJdId => {
        const employeeId = jdToEmployeeMap.get(reportJdId);
        if (employeeId) {
          subordinateIds.add(employeeId);
        }
        findSubordinates(reportJdId);
      });
    };
    
    findSubordinates(myJobDescriptionId);
    return subordinateIds;
  }, [orgChartData, myJobDescriptionId]);

  // Find CEO's job description ID (for C-Suite filtering)
  const ceoJobDescriptionId = useMemo(() => {
    if (!orgChartData) return null;
    // Try multiple approaches to find CEO:
    // 1. Find node with no reportsToJobDescriptionId (top of hierarchy)
    let ceoNode = orgChartData.find(node => !node.jobDescription.reportsToJobDescriptionId);
    // 2. Find node with title containing "CEO" or "Chief Executive"
    if (!ceoNode) {
      ceoNode = orgChartData.find(node => 
        node.jobDescription.title?.toLowerCase().includes('ceo') ||
        node.jobDescription.title?.toLowerCase().includes('chief executive officer')
      );
    }
    return ceoNode?.jobDescription.id || null;
  }, [orgChartData]);

  // Check if user can access this department
  const canAccessDepartment = useMemo(() => {
    // C-Suite is only accessible to CEO/admin users
    if (isCSuiteDepartment) {
      return hasCompanyWideView;
    }
    
    if (hasCompanyWideView) return true;
    
    // User can access if it's their own department
    if (myDepartment && decodedDepartment === myDepartment) return true;
    
    // Or if department has their subordinates
    if (orgChartData) {
      const hasSubordinateInDept = orgChartData.some(node => 
        node.jobDescription.department === decodedDepartment && 
        node.employee?.id && 
        getAllSubordinateIds.has(node.employee.id)
      );
      if (hasSubordinateInDept) return true;
    }
    
    return false;
  }, [hasCompanyWideView, myDepartment, decodedDepartment, orgChartData, getAllSubordinateIds, isCSuiteDepartment]);

  // Filter org chart data for this department based on access level
  const filteredDepartmentNodes = useMemo(() => {
    if (!orgChartData) return [];
    
    // Special handling for C-Suite virtual department
    if (isCSuiteDepartment) {
      // C-Suite only accessible to company-wide view users
      if (!hasCompanyWideView) return [];
      
      // Return direct reports to the CEO if we found the CEO
      if (ceoJobDescriptionId) {
        return orgChartData.filter(node => 
          node.jobDescription.reportsToJobDescriptionId === ceoJobDescriptionId
        );
      }
      
      // Fallback: return Chief-level positions (excluding CEO itself)
      return orgChartData.filter(node => 
        node.jobDescription.title?.toLowerCase().includes('chief') && 
        !node.jobDescription.title?.toLowerCase().includes('ceo') &&
        !node.jobDescription.title?.toLowerCase().includes('chief executive officer')
      );
    }
    
    // Get nodes for this department
    const deptNodes = orgChartData.filter(node => node.jobDescription.department === decodedDepartment);
    
    // If company-wide view, return all department nodes
    if (hasCompanyWideView) return deptNodes;
    
    // For non-company-wide users, filter to show:
    // 1. Employees in the same department (if this is user's department)
    // 2. Subordinates in this department
    return deptNodes.filter(node => {
      const employeeId = node.employee?.id;
      
      // Include if this is user's department
      if (myDepartment === decodedDepartment) return true;
      
      // Include if this is a subordinate
      if (employeeId && getAllSubordinateIds.has(employeeId)) return true;
      
      return false;
    });
  }, [orgChartData, decodedDepartment, hasCompanyWideView, myDepartment, getAllSubordinateIds, isCSuiteDepartment, ceoJobDescriptionId]);

  // Get monthly periods for the selected quarter
  const monthlyPeriods = useMemo(() => {
    const match = selectedPeriod.match(/Q(\d)\s+(\d{4})/);
    if (!match) return [];
    const quarter = parseInt(match[1]);
    const year = match[2];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
    const startMonth = (quarter - 1) * 3;
    return [
      `${monthNames[startMonth]} ${year}`,
      `${monthNames[startMonth + 1]} ${year}`,
      `${monthNames[startMonth + 2]} ${year}`,
    ];
  }, [selectedPeriod]);

  // Filter to only include months from March 2026 onwards (Monthly Incentive Goals feature start date)
  const validGoalMonths = useMemo(() => {
    return monthlyPeriods.filter(isMonthMarch2026OrLater);
  }, [monthlyPeriods]);

  // Calculate active months - for current quarter only show months up to current, past quarters show all
  const activeMonths = useMemo(() => {
    const now = new Date();
    const currentQuarter = `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`;
    const isCurrentQuarter = selectedPeriod === currentQuarter;
    
    if (isCurrentQuarter) {
      return monthlyPeriods.filter((month) => {
        const [monthName, year] = month.split(' ');
        const monthDate = new Date(`${monthName} 1, ${year}`);
        return monthDate <= now;
      });
    }
    return monthlyPeriods;
  }, [monthlyPeriods, selectedPeriod]);

  // Process employee data - use filtered department nodes for access control
  const employeesData = useMemo(() => {
    if (!filteredDepartmentNodes || filteredDepartmentNodes.length === 0) return { employees: [], metrics: null, goalMetrics: null };

    // Helper to check if period is pre-2026 (for filtering employees not yet hired)
    const periodYear = parseInt(selectedPeriod.match(/\d{4}/)?.[0] || '2026');
    const isPre2026 = periodYear < 2026;

    const deptNodes = filteredDepartmentNodes
      .filter(node => node.employee)
      // Filter out Wilma for pre-2026 periods (she started Jan 2026)
      .filter(node => !(isPre2026 && node.employee?.email?.toLowerCase() === 'wilma@westprop.com'));

    // Get employee IDs from filtered nodes
    const filteredEmployeeIds = new Set(
      deptNodes
        .filter(node => node.employee?.id)
        .map(node => node.employee!.id)
    );

    // Filter PEAR reviews for this period and only for filtered employees
    const periodPEARs = pearReviews?.filter(p => 
      p.reviewPeriod === selectedPeriod && 
      (p.status === 'submitted' || p.status === 'approved') &&
      p.employeeId && filteredEmployeeIds.has(p.employeeId)
    ) || [];
    
    // Filter SPEAR reviews for this quarter's months and only for filtered employees
    const periodSpears = allSpearReviews?.filter(sr => 
      monthlyPeriods.includes(sr.reviewPeriod) && 
      sr.userId && filteredEmployeeIds.has(sr.userId)
    ) || [];

    const employees: EmployeeScore[] = deptNodes.map(node => {
      const pearReview = periodPEARs.find(p => p.employeeId === node.employee?.id);
      
      // Get goals for this employee from SPEAR submissions
      const employeeSpears = periodSpears.filter(sr => sr.userId === node.employee?.id);
      const monthlyGoals = employeeSpears.map(spear => {
        const goal = spear.monthlyIncentiveGoal;
        const status = getGoalAchievementStatus(goal);
        return {
          spearId: spear.id || '',
          month: spear.reviewPeriod,
          shortMonth: spear.reviewPeriod.substring(0, 3), // "October 2025" -> "Oct"
          goalDescription: goal?.description,
          goalMeasure: goal?.measure,
          targetPercentage: goal?.targetPercentage,
          actualPercentage: goal?.actualPercentage,
          status,
          completedByUserName: goal?.completedByUserName,
        };
      });

      // Count goal statuses
      const goalsAchieved = monthlyGoals.filter(g => g.status === 'achieved').length;
      const goalsProgress = monthlyGoals.filter(g => g.status === 'progress').length;
      const goalsFailed = monthlyGoals.filter(g => g.status === 'failed').length;

      return {
        id: node.employee!.id,
        name: node.employee!.fullName,
        email: node.employee!.email,
        designation: node.jobDescription?.title,
        bscScore: node.latestBSC?.totalWeightedScore,
        bscGrade: node.latestBSC?.finalGrade,
        bscStatus: node.latestBSC?.status,
        pearScore: pearReview?.overallScore,
        pearGrade: pearReview?.finalGrade,
        pearStatus: pearReview?.status,
        hasReviews: !!(node.latestBSC || pearReview),
        monthlyGoals,
        goalsAchieved,
        goalsProgress,
        goalsFailed,
      };
    });

    // Calculate metrics
    const withBSC = employees.filter(e => e.bscScore !== undefined);
    const withPEAR = employees.filter(e => e.pearScore !== undefined);

    const avgBSC = withBSC.length > 0
      ? withBSC.reduce((sum, e) => sum + e.bscScore!, 0) / withBSC.length
      : 0;

    const avgPEAR = withPEAR.length > 0
      ? withPEAR.reduce((sum, e) => sum + e.pearScore!, 0) / withPEAR.length
      : 0;

    const topPerformer = withBSC.length > 0
      ? withBSC.reduce((max, curr) => (curr.bscScore! > (max.bscScore || 0)) ? curr : max, withBSC[0])
      : null;

    const needsAttention = employees.filter(e => e.bscScore !== undefined && e.bscScore < 80);

    // Goal metrics - using status-based counting
    // Filter to only include goals from March 2026 onwards (feature start date)
    const allGoals = employees.flatMap(e => 
      e.monthlyGoals.filter(g => g.goalDescription && isMonthMarch2026OrLater(g.month))
    );
    const achievedGoals = allGoals.filter(g => g.status === 'achieved');
    const progressGoals = allGoals.filter(g => g.status === 'progress');
    const failedGoals = allGoals.filter(g => g.status === 'failed');
    const pendingGoals = allGoals.filter(g => g.status === 'pending');
    const goalMetrics = {
      totalGoals: allGoals.length,
      achievedGoals: achievedGoals.length,
      progressGoals: progressGoals.length,
      failedGoals: failedGoals.length,
      pendingGoals: pendingGoals.length,
      completionRate: allGoals.length > 0 ? (achievedGoals.length / allGoals.length) * 100 : 0,
    };

    return {
      employees,
      metrics: {
        totalEmployees: employees.length,
        avgBSC,
        avgPEAR,
        bscSubmissions: withBSC.length,
        pearSubmissions: withPEAR.length,
        bscCompletion: employees.length > 0 ? (withBSC.length / employees.length) * 100 : 0,
        pearCompletion: employees.length > 0 ? (withPEAR.length / employees.length) * 100 : 0,
        topPerformer,
        needsAttentionCount: needsAttention.length,
      },
      goalMetrics,
    };
  }, [filteredDepartmentNodes, pearReviews, allSpearReviews, selectedPeriod, monthlyPeriods]);

  // Filter and sort employees
  const filteredEmployees = useMemo(() => {
    let result = [...employeesData.employees];

    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        e => e.name.toLowerCase().includes(term) ||
          e.email.toLowerCase().includes(term) ||
          e.designation?.toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal: any;
      let bVal: any;
      
      // Special handling for 'goals' sort - sort by achieved count, then progress
      if (sortField === 'goals') {
        aVal = a.goalsAchieved * 100 + a.goalsProgress * 10 - a.goalsFailed;
        bVal = b.goalsAchieved * 100 + b.goalsProgress * 10 - b.goalsFailed;
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }

      // Handle undefined values
      if (aVal === undefined) aVal = sortDirection === 'desc' ? -Infinity : Infinity;
      if (bVal === undefined) bVal = sortDirection === 'desc' ? -Infinity : Infinity;

      if (typeof aVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [employeesData.employees, searchTerm, sortField, sortDirection]);

  // Handlers
  const handlePeriodChange = (event: SelectChangeEvent) => {
    setSelectedPeriod(event.target.value);
  };

  const handleRefresh = () => {
    refetchPEAR();
    refetchOrgChart();
    refetchSpear();
    refetchCastingTool();
  };

  // Handle setting goal actual percentage
  const handleSetGoalPercentage = async (spearId: string, actualPercentage: number) => {
    try {
      await markGoalComplete({ id: spearId, body: { actualPercentage } }).unwrap();
      refetchSpear();
    } catch (error) {
      console.error('Error updating goal percentage:', error);
    }
  };

  // Cycle through statuses: pending -> achieved (100%) -> progress (90%) -> failed (50%) -> reset (undefined)
  const handleCycleGoalStatus = async (spearId: string, currentActual?: number) => {
    let newPercentage: number;
    if (currentActual === undefined || currentActual === null) {
      newPercentage = 100; // Set to achieved
    } else if (currentActual >= 100) {
      newPercentage = 90; // Set to progress
    } else if (currentActual >= 80) {
      newPercentage = 50; // Set to failed
    } else {
      newPercentage = 100; // Reset to achieved
    }
    await handleSetGoalPercentage(spearId, newPercentage);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleEmployeeClick = (employeeId: string) => {
    router.push(`/reviews/ceo-dashboard/employee/${employeeId}?period=${selectedPeriod}`);
  };

  // Check access
  if (!canAccessDepartment) {
    return (
      <PageWithHeader>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">Access Denied</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            You do not have permission to view this department.
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => router.push('/reviews/ceo-dashboard')}
            sx={{ mt: 2 }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => router.push('/reviews/ceo-dashboard')}
              sx={{ mb: 1 }}
            >
              Back to Dashboard
            </Button>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {decodedDepartment}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Department performance metrics and employee details
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Period"
                onChange={handlePeriodChange}
              >
                {availableQuarters.map((q) => (
                  <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : employeesData.metrics ? (
          <>
            {/* Metrics Row */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard
                  title="Employees"
                  value={employeesData.metrics.totalEmployees}
                  icon={<PeopleIcon />}
                  color="#1976d2"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard
                  title="Avg BSC Score"
                  value={employeesData.metrics.avgBSC > 0 ? `${employeesData.metrics.avgBSC.toFixed(1)}%` : 'N/A'}
                  subtitle={`${employeesData.metrics.bscSubmissions} of ${employeesData.metrics.totalEmployees} submitted`}
                  secondarySubtitle={`(${employeesData.metrics.bscCompletion.toFixed(0)}% completion)`}
                  icon={<AssessmentIcon />}
                  color={getScoreColor(employeesData.metrics.avgBSC)}
                  showWarning={employeesData.metrics.bscCompletion < 50}
                  warningText={employeesData.metrics.bscCompletion < 50 ? 'Low participation' : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard
                  title="Avg PEAR Score"
                  value={employeesData.metrics.avgPEAR > 0 ? `${employeesData.metrics.avgPEAR.toFixed(1)}%` : 'N/A'}
                  subtitle={`${employeesData.metrics.pearSubmissions} of ${employeesData.metrics.totalEmployees} submitted`}
                  secondarySubtitle={`(${employeesData.metrics.pearCompletion.toFixed(0)}% completion)`}
                  icon={<AssessmentIcon />}
                  color={getScoreColor(employeesData.metrics.avgPEAR)}
                  showWarning={employeesData.metrics.pearCompletion < 50}
                  warningText={employeesData.metrics.pearCompletion < 50 ? 'Low participation' : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard
                  title="Top Performer"
                  value={employeesData.metrics.topPerformer?.bscScore?.toFixed(1) + '%' || 'N/A'}
                  subtitle={employeesData.metrics.topPerformer?.name}
                  icon={<TrophyIcon />}
                  color="#FFD700"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <MetricCard
                  title="Needs Attention"
                  value={employeesData.metrics.needsAttentionCount}
                  subtitle="Below 80%"
                  icon={<WarningIcon />}
                  color={employeesData.metrics.needsAttentionCount > 0 ? '#ff9800' : '#4caf50'}
                />
              </Grid>
            </Grid>

            {/* Casting Tool Stats Row */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4}>
                <MetricCard
                  title="Casting Tool (4x4)"
                  value={`${castingToolMetrics.rate.toFixed(0)}%`}
                  subtitle={`${castingToolMetrics.submitted} of ${castingToolMetrics.total} submitted`}
                  secondarySubtitle="Current month"
                  icon={<GridViewIcon />}
                  color="#00bcd4"
                  showWarning={castingToolMetrics.rate < 50}
                  warningText={castingToolMetrics.rate < 50 ? 'Low participation' : undefined}
                />
              </Grid>
            </Grid>

            {/* Goal Distribution & Grade Distribution */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {validGoalMonths.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f5e9 100%)' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      🎯 Monthly Incentive Goals
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">
                          {employeesData.goalMetrics?.totalGoals || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Goals</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '2px solid #1976d2' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: '#1976d2' }}>
                          {employeesData.goalMetrics?.achievedGoals || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Achieved (100%+)</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '2px solid #ff9800' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: '#ff9800' }}>
                          {employeesData.goalMetrics?.progressGoals || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Progress (80-99%)</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#ffebee', borderRadius: 2, border: '2px solid #f44336' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: '#f44336' }}>
                          {employeesData.goalMetrics?.failedGoals || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Not Achieved (&lt;80%)</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              )}
              <Grid item xs={12} md={validGoalMonths.length > 0 ? 6 : 12}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Grade Distribution
                  </Typography>
                  <GradeDistributionMini employees={employeesData.employees} />
                </Paper>
              </Grid>
            </Grid>

            {/* Employee Goals Table */}
            {validGoalMonths.length > 0 && employeesData.goalMetrics && employeesData.goalMetrics.totalGoals > 0 && (
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Employee Goal Status by Month
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        {validGoalMonths.map(month => (
                          <TableCell key={month} align="center">{month.substring(0, 3)}</TableCell>
                        ))}
                        <TableCell align="center">Summary</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {employeesData.employees
                        .filter(emp => emp.monthlyGoals.some(g => g.goalDescription && isMonthMarch2026OrLater(g.month)))
                        .map(emp => (
                          <TableRow key={emp.id} hover>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                                  {getInitials(emp.name)}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">{emp.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{emp.designation}</Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            {validGoalMonths.map(month => {
                              const goal = emp.monthlyGoals.find(g => g.month === month);
                              if (!goal || !goal.goalDescription) {
                                return (
                                  <TableCell key={month} align="center">
                                    <Chip size="small" label="—" variant="outlined" sx={{ color: '#9e9e9e' }} />
                                  </TableCell>
                                );
                              }
                              
                              const statusConfig = {
                                achieved: { icon: <CheckCircleIcon />, label: `${goal.actualPercentage || 100}%`, bgColor: '#e3f2fd', borderColor: '#1976d2', textColor: '#1976d2' },
                                progress: { icon: <TrendingFlatIcon />, label: `${goal.actualPercentage}%`, bgColor: '#fff3e0', borderColor: '#ff9800', textColor: '#ff9800' },
                                failed: { icon: <CancelIcon />, label: `${goal.actualPercentage}%`, bgColor: '#ffebee', borderColor: '#f44336', textColor: '#f44336' },
                                pending: { icon: <PendingIcon />, label: 'Pending', bgColor: '#f5f5f5', borderColor: '#9e9e9e', textColor: '#9e9e9e' },
                              };
                              const config = statusConfig[goal.status];
                              
                              return (
                                <TableCell key={month} align="center">
                                  <Tooltip title={
                                    <Box>
                                      <Typography variant="body2" fontWeight="bold">Goal:</Typography>
                                      <Typography variant="body2">{goal.goalDescription}</Typography>
                                      {goal.goalMeasure && (
                                        <>
                                          <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>Measure:</Typography>
                                          <Typography variant="body2">{goal.goalMeasure}</Typography>
                                        </>
                                      )}
                                      {goal.actualPercentage !== undefined && (
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                          Actual: {goal.actualPercentage}% | Target: {goal.targetPercentage || 100}%
                                        </Typography>
                                      )}
                                      {goal.completedByUserName && (
                                        <Typography variant="caption">Evaluated by: {goal.completedByUserName}</Typography>
                                      )}
                                      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                                        Click to cycle status
                                      </Typography>
                                    </Box>
                                  }>
                                    <Chip
                                      size="small"
                                      icon={config.icon}
                                      label={config.label}
                                      onClick={() => handleCycleGoalStatus(goal.spearId, goal.actualPercentage)}
                                      sx={{ 
                                        cursor: 'pointer',
                                        bgcolor: config.bgColor,
                                        border: `2px solid ${config.borderColor}`,
                                        color: config.textColor,
                                        '& .MuiChip-icon': { color: config.textColor },
                                      }}
                                    />
                                  </Tooltip>
                                </TableCell>
                              );
                            })}
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                {emp.goalsAchieved > 0 && (
                                  <Chip size="small" label={emp.goalsAchieved} sx={{ bgcolor: '#1976d2', color: 'white', minWidth: 24 }} />
                                )}
                                {emp.goalsProgress > 0 && (
                                  <Chip size="small" label={emp.goalsProgress} sx={{ bgcolor: '#ff9800', color: 'white', minWidth: 24 }} />
                                )}
                                {emp.goalsFailed > 0 && (
                                  <Chip size="small" label={emp.goalsFailed} sx={{ bgcolor: '#f44336', color: 'white', minWidth: 24 }} />
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Employees Table */}
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                  Employees ({filteredEmployees.length})
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 300 }}
                />
              </Stack>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={60}>#</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'name'}
                          direction={sortField === 'name' ? sortDirection : 'asc'}
                          onClick={() => handleSort('name')}
                        >
                          Employee
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'designation'}
                          direction={sortField === 'designation' ? sortDirection : 'asc'}
                          onClick={() => handleSort('designation')}
                        >
                          Designation
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">
                        <TableSortLabel
                          active={sortField === 'bscScore'}
                          direction={sortField === 'bscScore' ? sortDirection : 'asc'}
                          onClick={() => handleSort('bscScore')}
                        >
                          BSC Score
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">BSC Status</TableCell>
                      <TableCell align="center">
                        <TableSortLabel
                          active={sortField === 'pearScore'}
                          direction={sortField === 'pearScore' ? sortDirection : 'asc'}
                          onClick={() => handleSort('pearScore')}
                        >
                          PEAR Score
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">PEAR Status</TableCell>
                      <TableCell align="center">
                        <TableSortLabel
                          active={sortField === 'goals'}
                          direction={sortField === 'goals' ? sortDirection : 'asc'}
                          onClick={() => handleSort('goals')}
                        >
                          Goals
                        </TableSortLabel>
                      </TableCell>
                      {/* Monthly Goal Columns */}
                      {activeMonths.map(month => (
                        <TableCell key={month} align="center" sx={{ minWidth: 80 }}>
                          <Typography variant="caption" fontWeight="bold">
                            {month.substring(0, 3)}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEmployees.map((employee, index) => (
                      <TableRow
                        key={employee.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEmployeeClick(employee.id)}
                      >
                        <TableCell>
                          <Chip
                            label={index + 1}
                            size="small"
                            sx={{
                              backgroundColor: index < 3 ? '#FFD700' : '#e0e0e0',
                              color: index < 3 ? 'black' : 'inherit',
                              fontWeight: 'bold',
                              minWidth: 32,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: '#1976d2' }}>
                              {getInitials(employee.name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {employee.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {employee.email}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>{employee.designation || 'N/A'}</TableCell>
                        <TableCell align="center">
                          {employee.bscScore !== undefined ? (
                            <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                              <Typography variant="body2">{employee.bscScore.toFixed(1)}%</Typography>
                              <Chip
                                label={employee.bscGrade}
                                size="small"
                                sx={{
                                  backgroundColor: getGradeColor(employee.bscGrade),
                                  color: 'white',
                                  minWidth: 36,
                                }}
                              />
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {employee.bscStatus ? (
                            <Chip
                              icon={employee.bscStatus === 'approved' ? <CheckCircleIcon /> : <PendingIcon />}
                              label={employee.bscStatus}
                              size="small"
                              color={employee.bscStatus === 'approved' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          ) : (
                            <Chip label="Not Started" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {employee.pearScore !== undefined ? (
                            <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                              <Typography variant="body2">{employee.pearScore.toFixed(1)}%</Typography>
                              <Chip
                                label={employee.pearGrade}
                                size="small"
                                sx={{
                                  backgroundColor: getGradeColor(employee.pearGrade),
                                  color: 'white',
                                  minWidth: 36,
                                }}
                              />
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {employee.pearStatus ? (
                            <Chip
                              icon={employee.pearStatus === 'approved' ? <CheckCircleIcon /> : <PendingIcon />}
                              label={employee.pearStatus}
                              size="small"
                              color={employee.pearStatus === 'approved' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          ) : (
                            <Chip label="Not Started" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {employee.goalsAchieved > 0 && (
                              <Tooltip title="Achieved (100%+)">
                                <Chip size="small" label={employee.goalsAchieved} sx={{ bgcolor: '#1976d2', color: 'white', minWidth: 24 }} />
                              </Tooltip>
                            )}
                            {employee.goalsProgress > 0 && (
                              <Tooltip title="Progress (80-99%)">
                                <Chip size="small" label={employee.goalsProgress} sx={{ bgcolor: '#ff9800', color: 'white', minWidth: 24 }} />
                              </Tooltip>
                            )}
                            {employee.goalsFailed > 0 && (
                              <Tooltip title="Not Achieved (<80%)">
                                <Chip size="small" label={employee.goalsFailed} sx={{ bgcolor: '#f44336', color: 'white', minWidth: 24 }} />
                              </Tooltip>
                            )}
                            {employee.goalsAchieved === 0 && employee.goalsProgress === 0 && employee.goalsFailed === 0 && (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        {/* Monthly Goal Cells */}
                        {activeMonths.map(month => {
                          const monthGoal = employee.monthlyGoals.find(g => g.month === month);
                          const status = monthGoal?.status || 'pending';
                          const hasGoal = !!monthGoal?.goalDescription;
                          return (
                            <TableCell key={month} align="center" onClick={(e) => e.stopPropagation()}>
                              {hasGoal ? (
                                <Tooltip title={
                                  <Box>
                                    <Typography variant="caption" fontWeight="bold">{monthGoal?.goalDescription}</Typography>
                                    {monthGoal?.goalMeasure && (
                                      <Typography variant="caption" display="block">Measure: {monthGoal.goalMeasure}</Typography>
                                    )}
                                    {monthGoal?.actualPercentage !== undefined && (
                                      <Typography variant="caption" display="block">Actual: {monthGoal.actualPercentage}%</Typography>
                                    )}
                                  </Box>
                                }>
                                  <Chip
                                    size="small"
                                    label={monthGoal?.actualPercentage !== undefined ? `${monthGoal.actualPercentage}%` : '?'}
                                    sx={{
                                      bgcolor: getGoalStatusColor(status),
                                      color: 'white',
                                      fontWeight: 'bold',
                                      minWidth: 48,
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => monthGoal?.spearId && handleCycleGoalStatus(monthGoal.spearId, monthGoal.actualPercentage)}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <IconButton size="small">
                            <NavigateNextIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredEmployees.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9 + activeMonths.length} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                            No employees found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">
              No data available for this department
            </Typography>
          </Box>
        )}
      </Box>
    </PageWithHeader>
  );
};

export default CEODepartmentView;
